import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const API_KEY = '4f8748a528a6ef84724a4cdadc5409f7';

function App() {
  const [airQualityData, setAirQualityData] = useState(null);
  const [historicalData, setHistoricalData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchCity, setSearchCity] = useState('Salt Lake City, US');
  const [position, setPosition] = useState([40.7608, -111.8910]); // Default to Salt Lake City, US
  const [clickedPosition, setClickedPosition] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Caching removed on request – always fetch fresh data

  const getAQICategory = (aqi) => {
    if (aqi <= 50) return { category: 'Good', class: 'aqi-good', description: 'Air quality is satisfactory, and air pollution poses little or no risk.' };
    if (aqi <= 100) return { category: 'Moderate', class: 'aqi-moderate', description: 'Air quality is acceptable. However, there may be a risk for some people, particularly those who are unusually sensitive to air pollution.' };
    if (aqi <= 150) return { category: 'Unhealthy for Sensitive Groups', class: 'aqi-unhealthy-sensitive', description: 'Members of sensitive groups may experience health effects. The general public is less likely to be affected.' };
    if (aqi <= 200) return { category: 'Unhealthy', class: 'aqi-unhealthy', description: 'Some members of the general public may experience health effects; members of sensitive groups may experience more serious health effects.' };
    if (aqi <= 300) return { category: 'Very Unhealthy', class: 'aqi-very-unhealthy', description: 'Health alert: The risk of health effects is increased for everyone.' };
    return { category: 'Hazardous', class: 'aqi-hazardous', description: 'Health warning of emergency conditions: everyone is more likely to be affected.' };
  };

  const getCityName = async (lat, lon) => {
    try {
      const response = await axios.get(
        `https://api.openweathermap.org/geo/1.0/reverse?lat=${lat}&lon=${lon}&limit=1&appid=${API_KEY}`
      );
      
      if (response.data && response.data.length > 0) {
        const { name, country, state } = response.data[0];
        return state ? `${name}, ${state}, ${country}` : `${name}, ${country}`;
      }
      return null;
    } catch (err) {
      console.error('Error getting city name:', err);
      return null;
    }
  };

  // Function to estimate AQI based on weather conditions
  const estimateAQIFromWeather = (weatherData) => {
    // This is a simplified estimation algorithm
    // In a real application, you'd want to use more sophisticated modeling
    
    let baseAQI = 50; // Base AQI (Good)
    
    // Adjust based on temperature (higher temps can increase pollution)
    if (weatherData.main.temp > 25) {
      baseAQI += 10;
    } else if (weatherData.main.temp < 0) {
      baseAQI += 5;
    }
    
    // Adjust based on humidity (high humidity can trap pollutants)
    if (weatherData.main.humidity > 80) {
      baseAQI += 15;
    } else if (weatherData.main.humidity < 30) {
      baseAQI += 5;
    }
    
    // Adjust based on wind speed (low wind can trap pollutants)
    if (weatherData.wind.speed < 2) {
      baseAQI += 20;
    } else if (weatherData.wind.speed > 10) {
      baseAQI -= 10;
    }
    
    // Adjust based on weather conditions
    const weatherMain = weatherData.weather[0]?.main?.toLowerCase() || '';
    if (weatherMain.includes('rain') || weatherMain.includes('snow')) {
      baseAQI -= 15; // Rain/snow can clean the air
    } else if (weatherMain.includes('fog') || weatherMain.includes('mist')) {
      baseAQI += 25; // Fog can trap pollutants
    }
    
    // Ensure AQI is within reasonable bounds
    return Math.max(0, Math.min(500, Math.round(baseAQI)));
  };

  const fetchForecastData = useCallback(async (lat, lon) => {
    try {
      console.log('Fetching air quality forecast data...');
      
      // Fetch 7-day weather forecast which includes air quality predictions
      const response = await axios.get(
        `https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=metric`
      );
      
      console.log('Forecast response:', response.data);
      
      if (response.data && response.data.list && response.data.list.length > 0) {
        // Process forecast data - group by day and calculate daily averages
        const dailyForecasts = {};
        
        response.data.list.forEach(item => {
          const date = new Date(item.dt * 1000);
          const dateKey = date.toLocaleDateString();
          
          if (!dailyForecasts[dateKey]) {
            dailyForecasts[dateKey] = {
              date: date,
              aqi: [],
              temperature: [],
              humidity: [],
              windSpeed: [],
              description: item.weather[0]?.description || 'Unknown'
            };
          }
          
          // Estimate AQI based on weather conditions and historical patterns
          // This is a simplified estimation - in reality, you'd want more sophisticated modeling
          const estimatedAQI = estimateAQIFromWeather(item);
          dailyForecasts[dateKey].aqi.push(estimatedAQI);
          dailyForecasts[dateKey].temperature.push(item.main.temp);
          dailyForecasts[dateKey].humidity.push(item.main.humidity);
          dailyForecasts[dateKey].windSpeed.push(item.wind.speed);
        });
        
        // Calculate daily averages and create forecast data
        const processedForecast = Object.values(dailyForecasts).map(day => ({
          date: day.date,
          aqi: Math.round(day.aqi.reduce((sum, val) => sum + val, 0) / day.aqi.length),
          temperature: Math.round(day.temperature.reduce((sum, val) => sum + val, 0) / day.temperature.length),
          humidity: Math.round(day.humidity.reduce((sum, val) => sum + val, 0) / day.humidity.length),
          windSpeed: Math.round(day.windSpeed.reduce((sum, val) => sum + val, 0) / day.windSpeed.length * 10) / 10,
          description: day.description
        }));
        
        console.log('Processed forecast data:', processedForecast);
        
        // Ensure we have at least some forecast data
        if (processedForecast.length === 0) {
          console.log('No processed forecast data available');
          return null;
        }
        
        return processedForecast;
      } else {
        console.log('No forecast data available in response');
        return null;
      }
    } catch (err) {
      console.error('Error fetching forecast data:', err);
      // Don't set error for forecast data as it's not critical
      return null;
    }
  }, []);

  const fetchAirQualityData = useCallback(async (lat, lon, cityName = null) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching air quality data for coordinates:', lat, lon);
      
      // Calculate timestamps for the last 7 days
      const endDate = Math.floor(Date.now() / 1000);
      const startDate = endDate - (7 * 24 * 60 * 60); // 7 days ago
      
      // Single API call to get both current and historical data
      const response = await axios.get(
        `https://api.openweathermap.org/data/2.5/air_pollution/history?lat=${lat}&lon=${lon}&start=${startDate}&end=${endDate}&appid=${API_KEY}`
      );
      
      console.log('Air quality data response:', response.data);
      
      const data = response.data;
      if (data.list && data.list.length > 0) {
        // Get the most recent data for current air quality
        const currentData = data.list[data.list.length - 1];
        const aqi = currentData.main.aqi;
        const components = currentData.components;
        
        // Get actual city name if not provided
        let actualCityName = cityName;
        if (!cityName || cityName === 'Your Location' || cityName === 'Selected Location') {
          actualCityName = await getCityName(lat, lon) || cityName || 'Current Location';
        }
        
        // Set current air quality data
        const airQualityInfo = {
          aqi,
          components,
          location: { lat, lon },
          cityName: actualCityName,
          timestamp: new Date().toLocaleString()
        };
        
        setAirQualityData(airQualityInfo);
        setPosition([lat, lon]);
        setClickedPosition(null); // Clear the temporary marker
        
        // Process historical data for chart
        const processedData = data.list.map(item => ({
          date: new Date(item.dt * 1000),
          aqi: item.main.aqi,
          pm2_5: item.components.pm2_5,
          pm10: item.components.pm10,
          no2: item.components.no2,
          so2: item.components.so2,
          o3: item.components.o3,
          co: item.components.co
        }));
        
        setHistoricalData(processedData);
        
        // Fetch forecast data (don't fail if it's not available)
        let forecastData = null;
        try {
          forecastData = await fetchForecastData(lat, lon);
          
          // Set forecast data in state
          setForecastData(forecastData);
        } catch (forecastError) {
          console.error('Error fetching forecast data:', forecastError);
          // Don't fail the entire request if forecast fails
          setForecastData(null);
        }
        
        // No caching – always fresh data
        
      } else {
        setError('No air quality data available for this location.');
        setHistoricalData(null);
        setForecastData(null);
      }
    } catch (err) {
      console.error('Error fetching air quality data:', err);
      if (err.response) {
        console.error('Error response:', err.response.data);
        setError(`API Error: ${err.response.status} - ${err.response.data.message || 'Unknown error'}`);
      } else if (err.request) {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('Failed to fetch air quality data. Please try again.');
      }
      setHistoricalData(null);
      setForecastData(null);
    } finally {
      setLoading(false);
    }
  }, [fetchForecastData]);

  // Function to manually refresh data (for user-initiated refreshes)
  const refreshData = useCallback(async (lat, lon, cityName) => {
    await fetchAirQualityData(lat, lon, cityName);
  }, [fetchAirQualityData]);

  const AirQualityChart = ({ data }) => {
    if (!data || data.length === 0) {
      return (
        <div style={{ 
          textAlign: 'center', 
          color: '#666', 
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginTop: '15px'
        }}>
          <p>No historical data available for the last 7 days</p>
          <small>Historical data may not be available for all locations</small>
        </div>
      );
    }

    // Local function to get AQI category for chart tooltips
    const getAQICategoryForChart = (aqi) => {
      if (aqi <= 50) return 'Good';
      if (aqi <= 100) return 'Moderate';
      if (aqi <= 150) return 'Unhealthy for Sensitive Groups';
      if (aqi <= 200) return 'Unhealthy';
      if (aqi <= 300) return 'Very Unhealthy';
      return 'Hazardous';
    };

    // Group data by day and calculate daily averages
    const dailyData = data.reduce((acc, item) => {
      const dateKey = item.date.toLocaleDateString();
      if (!acc[dateKey]) {
        acc[dateKey] = { aqi: [], count: 0 };
      }
      acc[dateKey].aqi.push(item.aqi);
      acc[dateKey].count++;
      return acc;
    }, {});

    // Sort dates chronologically
    const sortedDates = Object.keys(dailyData).sort((a, b) => new Date(a) - new Date(b));

    // Calculate daily averages
    const dailyAverages = sortedDates.map(date => {
      const day = dailyData[date];
      return Math.round(day.aqi.reduce((sum, val) => sum + val, 0) / day.count);
    });

    // Calculate dynamic Y-axis range based on actual data
    const minAQI = Math.min(...dailyAverages);
    const maxAQI = Math.max(...dailyAverages);
    const range = maxAQI - minAQI;
    
    // Set padding for better visualization (10% of range or minimum 5 units)
    const padding = Math.max(range * 0.1, 5);
    const yMin = Math.max(0, minAQI - padding);
    const yMax = maxAQI + padding;

    // Function to get color based on AQI value
    const getAQIColor = (aqi) => {
      if (aqi <= 50) return 'rgb(76, 175, 80)'; // Good - Green
      if (aqi <= 100) return 'rgb(255, 152, 0)'; // Moderate - Orange
      if (aqi <= 150) return 'rgb(255, 87, 34)'; // Unhealthy for Sensitive Groups - Light Red
      if (aqi <= 200) return 'rgb(244, 67, 54)'; // Unhealthy - Red
      if (aqi <= 300) return 'rgb(156, 39, 176)'; // Very Unhealthy - Purple
      return 'rgb(123, 31, 162)'; // Hazardous - Dark Purple
    };

    // Generate colors for each data point
    const pointColors = dailyAverages.map(aqi => getAQIColor(aqi));

    const chartData = {
      labels: sortedDates.map(date => {
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }),
      datasets: [
        {
          label: 'AQI',
          data: dailyAverages,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          tension: 0.3,
          fill: false,
          pointBackgroundColor: pointColors,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: '7-Day Air Quality History',
          color: '#333',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgb(75, 192, 192)',
          borderWidth: 1,
          callbacks: {
            label: function(context) {
              const aqi = context.parsed.y;
              const category = getAQICategoryForChart(aqi);
              return `AQI: ${aqi} (${category})`;
            }
          }
        },
      },
      scales: {
        y: {
          beginAtZero: false,
          min: yMin,
          max: yMax,
          ticks: {
            color: '#666',
            stepSize: Math.max(1, Math.ceil(range / 5)), // Dynamic step size
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
        },
        x: {
          ticks: {
            color: '#666',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
        },
      },
    };

    return (
      <div style={{ height: '200px', marginTop: '15px' }}>
        <Line data={chartData} options={options} />
      </div>
    );
  };

  const ForecastChart = ({ data }) => {
    console.log('ForecastChart received data:', data);
    
    if (!data || data.length === 0) {
      return (
        <div style={{ 
          textAlign: 'center', 
          color: '#666', 
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginTop: '15px'
        }}>
          <p>No forecast data available for the next 7 days</p>
          <small>Forecast data may not be available for all locations</small>
        </div>
      );
    }

    // Local function to get AQI category for chart tooltips
    const getAQICategoryForChart = (aqi) => {
      if (aqi <= 50) return 'Good';
      if (aqi <= 100) return 'Moderate';
      if (aqi <= 150) return 'Unhealthy for Sensitive Groups';
      if (aqi <= 200) return 'Unhealthy';
      if (aqi <= 300) return 'Very Unhealthy';
      return 'Hazardous';
    };

    // Sort data chronologically
    const sortedData = data.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Calculate dynamic Y-axis range based on actual data
    const aqiValues = sortedData.map(item => item.aqi);
    const minAQI = Math.min(...aqiValues);
    const maxAQI = Math.max(...aqiValues);
    const range = maxAQI - minAQI;
    
    // Set padding for better visualization (10% of range or minimum 5 units)
    const padding = Math.max(range * 0.1, 5);
    const yMin = Math.max(0, minAQI - padding);
    const yMax = maxAQI + padding;

    // Function to get color based on AQI value
    const getAQIColor = (aqi) => {
      if (aqi <= 50) return 'rgb(76, 175, 80)'; // Good - Green
      if (aqi <= 100) return 'rgb(255, 152, 0)'; // Moderate - Orange
      if (aqi <= 150) return 'rgb(255, 87, 34)'; // Unhealthy for Sensitive Groups - Light Red
      if (aqi <= 200) return 'rgb(244, 67, 54)'; // Unhealthy - Red
      if (aqi <= 300) return 'rgb(156, 39, 176)'; // Very Unhealthy - Purple
      return 'rgb(123, 31, 162)'; // Hazardous - Dark Purple
    };

    // Generate colors for each data point
    const pointColors = aqiValues.map(aqi => getAQIColor(aqi));

    const chartData = {
      labels: sortedData.map(item => {
        const d = new Date(item.date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }),
      datasets: [
        {
          label: 'Forecasted AQI',
          data: aqiValues,
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.1)',
          tension: 0.3,
          fill: false,
          pointBackgroundColor: pointColors,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7,
          borderDash: [5, 5], // Dashed line for forecast
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: '7-Day Air Quality Forecast',
          color: '#333',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgb(255, 99, 132)',
          borderWidth: 1,
          callbacks: {
            label: function(context) {
              const aqi = context.parsed.y;
              const category = getAQICategoryForChart(aqi);
              const item = sortedData[context.dataIndex];
              return [
                `AQI: ${aqi} (${category})`,
                `Temp: ${item.temperature}°C`,
                `Humidity: ${item.humidity}%`,
                `Wind: ${item.windSpeed} m/s`
              ];
            }
          }
        },
      },
      scales: {
        y: {
          beginAtZero: false,
          min: yMin,
          max: yMax,
          ticks: {
            color: '#666',
            stepSize: Math.max(1, Math.ceil(range / 5)), // Dynamic step size
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
        },
        x: {
          ticks: {
            color: '#666',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
          },
        },
      },
    };

    return (
      <div style={{ height: '200px', marginTop: '15px' }}>
        <Line data={chartData} options={options} />
      </div>
    );
  };

    const handleSearch = async () => {
    if (!searchCity.trim()) {
      setError('Please enter a city name.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setShowSuggestions(false);
    
    try {
      console.log('Searching for city:', searchCity);
      
      const url = `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(searchCity)}&limit=1&appid=${API_KEY}`;
      console.log('API URL:', url);
      
      const response = await axios.get(url);
      
      console.log('Geocoding response:', response.data);
      
      if (!response.data || response.data.length === 0) {
        setError(`City "${searchCity}" not found. Please try a different city name.`);
        setLoading(false);
        return;
      }
      const cityData = response.data[0];
      
      const { lat, lon, name, country } = cityData;
      const cityName = `${name}, ${country}`;
      console.log(`Found city: ${cityName} at coordinates: ${lat}, ${lon}`);
      await fetchAirQualityData(lat, lon, cityName);
    } catch (err) {
      console.error('Error searching for city:', err);
      if (err.response) {
        console.error('Error response:', err.response.data);
        setError(`API Error: ${err.response.status} - ${err.response.data.message || 'Unknown error'}`);
      } else if (err.request) {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('Failed to find city. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInput = async (value) => {
    setSearchCity(value);
    
    if (value.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    try {
      const response = await axios.get(
        `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(value)}&limit=5&appid=${API_KEY}`
      );
      
      if (response.data && response.data.length > 0) {
        const citySuggestions = response.data.map(city => ({
          name: city.name,
          country: city.country,
          state: city.state,
          displayName: city.state ? `${city.name}, ${city.state}, ${city.country}` : `${city.name}, ${city.country}`
        }));
        setSuggestions(citySuggestions);
        setShowSuggestions(true);
        
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    setSearchCity(suggestion.displayName);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Automatically search for the selected city
    setLoading(true);
    setError(null);
    
    try {
      console.log('Auto-searching for city:', suggestion.displayName);
      const url = `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(suggestion.displayName)}&limit=1&appid=${API_KEY}`;
      console.log('API URL:', url);
      
      const response = await axios.get(url);
      
      console.log('Geocoding response:', response.data);
      
      if (response.data && response.data.length > 0) {
        const { lat, lon, name, country } = response.data[0];
        const cityName = `${name}, ${country}`;
        console.log(`Found city: ${cityName} at coordinates: ${lat}, ${lon}`);
        await fetchAirQualityData(lat, lon, cityName);
      } else {
        setError(`City "${suggestion.displayName}" not found. Please try a different city name.`);
      }
    } catch (err) {
      console.error('Error auto-searching for city:', err);
      if (err.response) {
        console.error('Error response:', err.response.data);
        setError(`API Error: ${err.response.status} - ${err.response.data.message || 'Unknown error'}`);
      } else if (err.request) {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('Failed to find city. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMapClick = useCallback((e) => {
    const { lat, lng } = e.latlng;
    console.log('Map clicked at coordinates:', lat, lng);
    console.log('Click event details:', e);
    
    setClickedPosition([lat, lng]);
    setLoading(true);
    setError(null);
    fetchAirQualityData(lat, lng, 'Selected Location');
  }, [fetchAirQualityData]);

  // Separate component for handling map events
  const MapClickHandler = () => {
    useMapEvents({
      click: (e) => {
        console.log('MapClickHandler: Map clicked at coordinates:', e.latlng);
        handleMapClick(e);
      },
    });
    return null;
  };

  useEffect(() => {
    // Always start with Salt Lake City, US
    console.log('App starting with Salt Lake City, US');
    fetchAirQualityData(40.7608, -111.8910, 'Salt Lake City, US');
  }, [fetchAirQualityData]);

  return (
    <div className="App">
             {/* Logo in top left */}
       <div 
         style={{
           position: 'absolute',
           top: '20px',
           left: '20px',
           zIndex: 10000,
           background: 'rgba(255, 255, 255, 0.95)',
           borderRadius: '8px',
           padding: '10px',
           boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
           border: '1px solid rgba(0, 0, 0, 0.1)'
         }}
       >
         <img 
           src={require('./logo.png')} 
           alt="Air Quality Map Logo" 
           style={{
             height: '40px',
             width: 'auto',
             display: 'block'
           }}
         />
       </div>
      

      <MapContainer
        key={`${position[0]}-${position[1]}`}
        center={position}
        zoom={10}
        style={{ height: '100vh', width: '100%' }}
        whenCreated={(map) => {
          console.log('Map created, click events should work');
        }}
      >
        <MapClickHandler />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {airQualityData && (
          <Marker position={position}>
            <Popup>
              <div>
                <h3>Air Quality Index: {airQualityData.aqi}</h3>
                <p>Click anywhere on the map to check air quality</p>
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* Temporary marker for clicked position */}
        {clickedPosition && (
          <Marker position={clickedPosition}>
            <Popup>
              <div>
                <h3>Loading...</h3>
                <p>Fetching air quality data for clicked location...</p>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Search Container */}
      <div className="search-container">
        <div className="search-input-container">
          <input
            type="text"
            className="search-input"
            placeholder="Enter city name..."
            value={searchCity}
            onChange={(e) => handleSearchInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            onFocus={() => searchCity.trim().length >= 2 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          />
          
          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="suggestions-dropdown">
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="suggestion-item"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion.displayName}
                </div>
              ))}
            </div>
          )}
        </div>
        
                 <button className="search-button" onClick={handleSearch}>
           Search
         </button>
      </div>

             {/* Air Quality Info Panel */}
               {airQualityData && (
          <div className="air-quality-info">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h2>{airQualityData.cityName}</h2>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button 
                  onClick={() => refreshData(airQualityData.location.lat, airQualityData.location.lon, airQualityData.cityName)}
                  style={{
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    padding: '5px 10px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                  title="Refresh data"
                >
                  🔄 Refresh
                </button>
                {/* Cache controls removed */}
              </div>
            </div>
           {(() => {
            const aqiInfo = getAQICategory(airQualityData.aqi);
            return (
              <div className={`aqi-value ${aqiInfo.class}`}>
                AQI: {airQualityData.aqi}
                <br />
                <small>{aqiInfo.category}</small>
              </div>
            );
          })()}
          
          <p><strong>PM2.5:</strong> {airQualityData.components.pm2_5} μg/m³</p>
          <p><strong>PM10:</strong> {airQualityData.components.pm10} μg/m³</p>
          <p><strong>NO₂:</strong> {airQualityData.components.no2} μg/m³</p>
          <p><strong>SO₂:</strong> {airQualityData.components.so2} μg/m³</p>
          <p><strong>O₃:</strong> {airQualityData.components.o3} μg/m³</p>
          <p><strong>CO:</strong> {airQualityData.components.co} μg/m³</p>
          
          <p><strong>Last Updated:</strong> {airQualityData.timestamp}</p>
          
          {/* Air Quality History Chart */}
          <AirQualityChart data={historicalData} />
          
           {/* Air Quality Forecast Chart */}
           <ForecastChart data={forecastData} />
          
          <p style={{ fontSize: '0.8rem', marginTop: '10px', color: '#888' }}>
            Click anywhere on the map to check air quality for that location
          </p>
          
           {/* Cache removed */}
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div className="loading">
          <h3>Loading air quality data...</h3>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error">
          <h3>Error</h3>
          <p>{error}</p>
          <button 
            onClick={() => setError(null)}
            style={{ 
              marginTop: '10px', 
              padding: '5px 10px', 
              background: 'white', 
              border: 'none', 
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

export default App; 